import { LinkOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { SortOrder } from 'antd/lib/table/interface';
import config from 'config';
import { Routes } from 'config/routes';
import { format } from 'date-fns';
import { DvmExamListSort, DvmExamOutput, Order } from 'gql/types';
import { ISortOption } from 'modules/common/interfaces';
import { generatePath } from 'modules/common/utils/generatePath';
import { Link } from 'react-router-dom';
import { getTranslation } from 'utils/getTranslation';

import styles from './styles.module.scss';

const t = getTranslation('donor.GeneralDvmExamTable');

type ColumnsProps = {
  showSorterTooltip?: boolean;
  sortOptions: ISortOption<DvmExamListSort>[];
};

export const getColumns = ({
  showSorterTooltip,
  sortOptions,
}: ColumnsProps): ColumnsType<DvmExamOutput> => {
  const getDefaultSort = (
    fieldName: DvmExamListSort
  ): SortOrder | undefined => {
    const sortOption = sortOptions.find((s) => s.fieldName === fieldName);
    if (sortOption) {
      return sortOption.order === Order.Asc ? 'ascend' : 'descend';
    }
    return undefined;
  };
  return [
    {
      title: t('donorId'),
      key: DvmExamListSort.DonorId,
      render: (row: DvmExamOutput) => row.donor.shortId,
      align: 'center',
      sorter: true,
      sortOrder: getDefaultSort(DvmExamListSort.DonorId),
      showSorterTooltip,
    },
    {
      title: t('petName'),
      key: DvmExamListSort.PetName,
      render: ({ donor }: DvmExamOutput) => (
        <Link to={generatePath(Routes.DonorsProfile, { id: donor.shortId })}>
          {donor.name}
        </Link>
      ),
      align: 'center',
      sorter: true,
      sortOrder: getDefaultSort(DvmExamListSort.PetName),
      showSorterTooltip,
    },
    {
      title: t('lastName'),
      key: DvmExamListSort.LastName,
      render: (row: DvmExamOutput) => row.donor.user?.lastName,
      align: 'center',
      sorter: true,
      sortOrder: getDefaultSort(DvmExamListSort.LastName),
      showSorterTooltip,
    },
    {
      title: t('nextExamDue'),
      key: DvmExamListSort.NextExamDue,
      render: (row: DvmExamOutput) =>
        row.nextExamDue
          ? format(new Date(row.nextExamDue), config.DATE_FORMATS.DATE)
          : '-',
      align: 'center',
      sorter: true,
      sortOrder: getDefaultSort(DvmExamListSort.NextExamDue),
      showSorterTooltip,
    },
    {
      title: t('region'),
      key: DvmExamListSort.Region,
      render: (row: DvmExamOutput) => row.donorRegion || '-',
      align: 'center',
      sorter: true,
      sortOrder: getDefaultSort(DvmExamListSort.Region),
      showSorterTooltip,
    },
    {
      title: t('location'),
      key: DvmExamListSort.Location,
      render: (row: DvmExamOutput) => row.donorLocation || '-',
      align: 'center',
      sorter: true,
      sortOrder: getDefaultSort(DvmExamListSort.Location),
      showSorterTooltip,
    },
    {
      title: t('veterinarian'),
      key: DvmExamListSort.Veterinarian,
      render: (row: DvmExamOutput) =>
        `${row.performedBy.firstName} ${row.performedBy.lastName}`,
      align: 'center',
      sorter: true,
      sortOrder: getDefaultSort(DvmExamListSort.Veterinarian),
      showSorterTooltip,
    },
    {
      title: t('link'),
      render: (row: DvmExamOutput) => (
        <Link
          to={generatePath(Routes.DonorsDvmExam, { id: row.donor.shortId })}
        >
          <LinkOutlined className={styles.linkIcon} />
        </Link>
      ),
      align: 'center',
    },
  ];
};
